import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Tooltip,
  Snackbar,
  Alert,
  Avatar,
  LinearProgress,
  alpha,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  CameraAlt,
  Delete,
  Save,
  CloudUpload,
  CheckCircle,
  Close,
  LocationOn,
  DirectionsCar,
  Warning,
  Description,
  ExpandMore,
  AccessTime
} from '@mui/icons-material';
import { buildAzureImageUrl, caseService } from '../services/api';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './LanguageSwitcher';
// Import company logo
import logo from '../assets/logo.svg';

// Map photo types to their respective image paths
const getCarIconPath = (photoType) => {
  console.log('Photo type requested:', photoType);

  // Handle different variations of chassis naming
  if (photoType && photoType.toLowerCase().includes('chassis')) {
    console.log('Chassis photo type detected, using chassis image');
    return '/images/car-chassis.png';
  }

  const iconMap = {
    front_windscreen: '/images/car-front-view.png',
    front_left_corner: '/images/car-front-left-corner.png',
    rear_left_corner: '/images/car-rear-left-corner.png',
    rear_right_corner: '/images/car-rear-right-corner.png',
    front_right_corner: '/images/car-front-right-corner.png',
    odometer: '/images/car-dashboard.png',
    chassis_number: '/images/car-chassis.png',
    chassis: '/images/car-chassis.png',
    'chassis number': '/images/car-chassis.png',
    'chassis_no': '/images/car-chassis.png',
    'chassis no': '/images/car-chassis.png',
    // Add mappings for other photo types as needed
  };

  const imagePath = iconMap[photoType] || '/images/car-front-view.png';
  console.log('Using image path:', imagePath);
  return imagePath;
};

const PHOTO_ORDER = ['front_windscreen', 'front_left_corner', 'rear_left_corner', 'rear_right_corner', 'front_right_corner', 'odometer', 'chassis_number'];


// Format date function
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleDateString();
};

const KeyValueGrid = ({ items }) => (
  <Box component="dl" sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }, m: 0 }}>
    {items.map(({ label, value }) => (
      <Box key={label} component="div" sx={{ display: 'grid', gridTemplateColumns: 'minmax(120px, 42%) 1fr', gap: 1.5, px: 2, py: 1.25, borderBottom: 1, borderColor: 'divider', minWidth: 0 }}>
        <Typography component="dt" variant="body2" color="text.secondary">{label}</Typography>
        <Typography component="dd" variant="body2" fontWeight={600} sx={{ m: 0, overflowWrap: 'anywhere' }}>{value || 'N/A'}</Typography>
      </Box>
    ))}
  </Box>
);



const ReassignmentPage = () => {
  const { referenceNumber } = useParams();
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const [caseData, setCaseData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openInstructions, setOpenInstructions] = useState(true);
  const [capturedImages, setCapturedImages] = useState({});
  const [currentPhoto, setCurrentPhoto] = useState(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [cameraReady, setCameraReady] = useState(false);
  const [stream, setStream] = useState(null);

  // State for reassigned images
  const [reassignedImages, setReassignedImages] = useState([]);
  const [remarks, setRemarks] = useState({});
  const [showRemarksDialog, setShowRemarksDialog] = useState(true);
  const [maxVersion, setMaxVersion] = useState('2'); // Default to version 2

  // New state variables for enhanced functionality
  const [location, setLocation] = useState({ latitude: null, longitude: null, locationName: 'Fetching location...' });
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [capturedImageData, setCapturedImageData] = useState(null);
  const [capturedAiImageData, setCapturedAiImageData] = useState(null);
  const [syncingImage, setSyncingImage] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [redirectToProcessed, setRedirectToProcessed] = useState(false);

  // Check case status and redirect if needed
  useEffect(() => {
    const checkCaseStatus = async () => {
      try {
        setLoading(true);
        console.log('Checking case status for:', referenceNumber);

        // Get case data by reference number using the API service
        const response = await caseService.getCaseByRefNo(referenceNumber);
        console.log('Case data fetched:', response);

        // Handle different response structures
        let caseData;
        if (response && response.status === 'success' && response.data && response.data.case) {
          // New API structure: data is nested under data.case
          caseData = response.data.case;
        } else if (response && response.status) {
          // Old API structure: data is directly in the response
          caseData = response;
        } else {
          throw new Error('Invalid response structure');
        }

        // Debug log to check case data structure
        console.log('Case data structure:', caseData);
        console.log('Policy number:', caseData.policy_number);

        // Set the case data in state
        setCaseData(caseData);

        // If status is not "Reassigned", redirect to appropriate page
        if (caseData.status !== 'Reassigned') {
          console.log('Case status is not Reassigned:', caseData.status);
          if (caseData.status === 'Open') {
            // Redirect to inspection page using React Router
            window.location.href = `/inspection/${referenceNumber}`;
            return;
          } else if (caseData.status === 'Processed') {
            setRedirectToProcessed(true);
            return;
          } else {
            window.location.href = `/`;
            return;
          }
        }

        // Get the maximum version for this case
        try {
          console.log('Getting max version for case:', referenceNumber);
          const versionResponse = await caseService.getMaxVersion(referenceNumber);
          const maxVersion = versionResponse.maxVersion || '2';
          console.log('Max version for case:', maxVersion);

          // Store the max version in component state
          setMaxVersion(maxVersion);
        } catch (versionError) {
          console.error('Error getting max version:', versionError);
          // Default to version 2 if we can't get the max version
          setMaxVersion('2');
        }

        // Fetch reassigned images
        await fetchReassignedImages(referenceNumber);
      } catch (err) {
        console.error('Error checking case status:', err);
        setError('Failed to load case data. The reference link may be invalid or expired.');
      } finally {
        setLoading(false);
      }
    };

    if (referenceNumber) {
      checkCaseStatus();
      getLocation();
    } else {
      setError(t('inspection.error.invalidReference'));
      setLoading(false);
    }
  }, [referenceNumber, i18n.language]);

  // Get user's current location
  const getLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setLocation(prev => ({ ...prev, latitude, longitude }));

          // Try to get location name using reverse geocoding
          fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`)
            .then(response => response.json())
            .then(data => {
              const locationName = data.display_name ?
                data.display_name.split(',').slice(0, 2).join(', ') :
                `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`;
              setLocation(prev => ({ ...prev, locationName }));
            })
            .catch(err => {
              console.error('Error getting location name:', err);
              setLocation(prev => ({
                ...prev,
                locationName: `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`
              }));
            });
        },
        (error) => {
          console.error('Error getting location:', error);
          setLocation(prev => ({ ...prev, locationName: 'Location unavailable' }));
        },
        { enableHighAccuracy: true, timeout: 5000, maximumAge: 0 }
      );
    } else {
      setLocation(prev => ({ ...prev, locationName: 'Geolocation not supported' }));
    }
  };

  // Fetch reassigned images with latest version from casemediacapture table
  const fetchReassignedImages = async (refNo = null) => {
    try {
      // Use provided refNo or fall back to caseData.web_reference_number
      const referenceNumber = refNo || caseData?.web_reference_number;

      if (!referenceNumber) {
        console.error('No reference number provided for fetching images');
        return;
      }

      console.log('Fetching reassigned images for:', referenceNumber);

      // Add cache-busting parameter to prevent browser caching
      const timestamp = new Date().getTime();

      // Get reassigned images using the API service
      const response = await caseService.getReassignedImages(referenceNumber, timestamp);

      // Log the entire API response for debugging
      console.log('Raw API response:', JSON.stringify(response, null, 2));

      // Validate the API response structure
      if (!response) {
        console.error('API returned null or undefined response');
        return;
      }

      // Determine if response is an array directly or has a data property
      const imageData = Array.isArray(response) ? response : (response.data || []);

      if (imageData && imageData.length > 0) {
        console.log('Reassigned images fetched:', imageData.length);

        const reassignedImagesArray = [];
        const remarksByMediaId = {};

        // Process each image from the API response
        imageData.forEach(img => {
          console.log('Processing image object:', img);

          // Get photo type with fallbacks for different property name cases
          let photoType = img.photoType || img.phototype || img.PhotoType;

          // If phototype is not available, try to extract from fileurl or azureurl
          if (!photoType) {
            if ((img.fileurl || img.fileUrl) && (img.fileurl || img.fileUrl).includes('/')) {
              const fileUrl = img.fileurl || img.fileUrl;
              const fileUrlParts = fileUrl.split('/');
              const filename = fileUrlParts[fileUrlParts.length - 1];
              photoType = filename.split('.')[0];
            } else if ((img.azureurl || img.azureUrl) && (img.azureurl || img.azureUrl).includes('/')) {
              const azureUrl = img.azureurl || img.azureUrl;
              const parts = azureUrl.split('/');
              photoType = parts[parts.length - 1].split('.')[0];
            } else {
              photoType = `image_${img.mediaid || img.id || Math.random().toString(36).substring(7)}`;
            }
          }

          // Format the photo type to be more user-friendly
          let displayPhotoType = photoType;
          if (displayPhotoType && displayPhotoType !== 'unknown') {
            // Replace underscores with spaces and capitalize words
            displayPhotoType = displayPhotoType
              .replace(/_/g, ' ')
              .replace(/\b\w/g, c => c.toUpperCase());
          } else if (displayPhotoType === 'unknown') {
            displayPhotoType = 'Unknown Image';
          }

          // Extract remarks if available
          const id = img.id || img.Id || img.ID || img.mediaid;
          const remark = img.remark || img.Remark;
          const remarks = img.remarks || img.Remarks;

          // Check for both 'remark' and 'remarks' properties since API might use either
          if (remark) {
            remarksByMediaId[id] = remark;
            console.log(`Found remark for ${photoType}:`, remark);
          } else if (remarks) {
            remarksByMediaId[id] = remarks;
            console.log(`Found remarks for ${photoType}:`, remarks);
          }

          // Get Azure URL with fallbacks for different property name cases
          const azureUrl = img.azureUrl || img.azureurl || img.AzureUrl || img.AZUREURL || img.azure_url;

          // Get file URL with fallbacks
          let imageUrl = img.fileUrl || img.fileurl || img.FileUrl || img.file_url;

          const version = img.version || img.Version || '2';

          // Format version with 'V' prefix if not already present
          const formattedVersion = version.toString().startsWith('V') ? version.toString() : `V${version}`;

          if (azureUrl) {
            console.log(`Using Azure URL for ${photoType}:`, azureUrl);
          } else {
            // Only use local URL as fallback if Azure URL is not available
            console.log(`Azure URL not available, using file URL for ${photoType}:`, imageUrl);

            // Use a direct path to the public folder which is accessible from the browser
            const filename = `${photoType}.jpg`;
            imageUrl = `/uploads/${referenceNumber}/${formattedVersion}/${filename}`;

            console.log(`Using direct public folder URL as fallback: ${imageUrl}`);
          }

          // Debug the image URLs
          console.log(`Creating image object for ${photoType}:`);
          console.log(`- Azure URL: ${azureUrl}`);
          console.log(`- Image URL: ${imageUrl}`);

          // Add to the reassignedImagesArray
          reassignedImagesArray.push({
            id: id,
            photoType: displayPhotoType,
            originalPhotoType: photoType,
            version: parseInt(version),
            status: 'Reassigned',
            imageUrl: imageUrl,
            azureUrl: azureUrl  // Include the Azure URL in the object
          });
        });

        // Set state with the processed data
        reassignedImagesArray.sort((left, right) => {
          const leftIndex = PHOTO_ORDER.indexOf(left.originalPhotoType);
          const rightIndex = PHOTO_ORDER.indexOf(right.originalPhotoType);
          return (leftIndex < 0 ? PHOTO_ORDER.length : leftIndex) - (rightIndex < 0 ? PHOTO_ORDER.length : rightIndex);
        });
        setReassignedImages(reassignedImagesArray);
        setRemarks(remarksByMediaId);

        console.log('Processed reassigned images:', reassignedImagesArray.length);
        console.log('Reassigned images details:', JSON.stringify(reassignedImagesArray, null, 2));
        console.log('Extracted remarks:', remarksByMediaId);
      } else {
        console.log('No reassigned images found in the response');
        setReassignedImages([]);
        setRemarks({});
      }
    } catch (error) {
      console.error('Error fetching reassigned images:', error);
    }
  };

  // Handle camera open with comprehensive error handling and permissions check
  const handleOpenCamera = async (photoId) => {
    setCurrentPhoto(photoId);
    setCameraReady(false);
    setCameraOpen(true);

    console.log('Attempting to access camera...');

    // Check if we're in a secure context (HTTPS or localhost)
    if (!window.isSecureContext) {
      console.error('Not in a secure context. Camera access requires HTTPS.');
      alert('Camera access requires a secure connection (HTTPS). Please ensure you are accessing this application via HTTPS.');
      handleCloseCamera();
      return;
    }

    try {
      // Request camera access with constraints for better quality
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'environment', // Prefer back camera
          width: { ideal: 1920 },
          height: { ideal: 1080 },
          aspectRatio: { ideal: 16 / 9 }
        }
      });

      // Store the stream and set it as the video source
      setStream(mediaStream);

      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
        videoRef.current.onloadedmetadata = () => {
          videoRef.current.play()
            .then(() => setCameraReady(true))
            .catch(error => console.error('Error playing video:', error));
        };
      } else {
        console.error('Video element not found');
        throw new Error('Video element not found');
      }
    } catch (err) {
      console.error('Error accessing camera:', err);

      // Provide specific error messages based on the error
      if (err.name === 'NotAllowedError') {
        alert('Camera access denied. Please allow camera access in your browser settings to continue.');
      } else if (err.name === 'NotFoundError') {
        alert('No camera found. Please ensure your device has a working camera.');
      } else if (err.name === 'NotReadableError') {
        alert('Camera is in use by another application. Please close other applications that might be using the camera.');
      } else {
        alert(`Failed to access camera: ${err.message || 'Unknown error'}`);
      }

      handleCloseCamera();
    }
  };

  // Handle closing the camera
  const handleCloseCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    setCameraReady(false);
    setCameraOpen(false);
    setShowConfirmDialog(false);
    setCapturedImageData(null);
    setCapturedAiImageData(null);
  };

  const pauseCameraForReview = () => {
    const activeStream = videoRef.current?.srcObject || stream;
    activeStream?.getTracks().forEach(track => track.stop());
    if (videoRef.current) videoRef.current.srcObject = null;
    setStream(null);
    setCameraReady(false);
    setCameraOpen(false);
  };



  // Handle capturing an image
  const handleCaptureImage = () => {
    if (!videoRef.current || !canvasRef.current) {
      console.error('Video or canvas element not found');
      return;
    }

    try {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      const context = canvas.getContext('2d');

      // Set canvas dimensions to match video dimensions
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      // Draw the video frame to the canvas
      context.drawImage(video, 0, 0, canvas.width, canvas.height);

      // Keep a clean frame for AI before adding the audit stamp.
      const aiImageData = canvas.toDataURL('image/jpeg', 0.8);
      const capturedAt = new Date().toLocaleString();
      const latitude = typeof location.latitude === 'number' ? location.latitude.toFixed(6) : '0.000000';
      const longitude = typeof location.longitude === 'number' ? location.longitude.toFixed(6) : '0.000000';
      context.fillStyle = 'rgba(0, 0, 0, 0.5)';
      context.fillRect(0, canvas.height - 60, canvas.width, 60);
      context.fillStyle = 'white';
      context.font = '14px Arial';
      context.fillText(`Date: ${capturedAt}`, 10, canvas.height - 40);
      context.fillText(`Location: ${latitude}, ${longitude}`, 10, canvas.height - 15);

      // Store stamped and clean versions separately.
      const imageData = canvas.toDataURL('image/jpeg', 0.8);
      setCapturedImageData(imageData);
      setCapturedAiImageData(aiImageData);
      setShowConfirmDialog(true);
      pauseCameraForReview();

      console.log('Image captured successfully');
    } catch (err) {
      console.error('Error capturing image:', err);
      alert('Failed to capture image. Please try again.');
    }
  };

  const handleRetakeImage = () => {
    const photoId = currentPhoto;
    setShowConfirmDialog(false);
    setCapturedImageData(null);
    setCapturedAiImageData(null);
    if (photoId) handleOpenCamera(photoId);
  };

  // Handle confirming the captured image
  const handleConfirmImage = async () => {
    if (!capturedImageData || !currentPhoto) {
      console.error('No image data or photo type');
      return;
    }

    try {
      console.log(`Confirming image for ${currentPhoto}`);

      // Show processing message
      setSnackbar({
        open: true,
        message: 'Processing image...',
        severity: 'info'
      });

      const syncResponse = await syncImageWithServer(currentPhoto, capturedImageData, capturedAiImageData);

      // After successful sync, fetch the latest images from the server
      if (syncResponse && syncResponse.status === 'success') {
        console.log('Image synced successfully, refreshing image data from server');
        await fetchReassignedImages();
      }

      handleCloseCamera();

      // Show success message
      setSnackbar({
        open: true,
        message: 'Image captured and saved successfully',
        severity: 'success'
      });
    } catch (err) {
      console.error('Error confirming image:', err);
      setSnackbar({
        open: true,
        message: 'Failed to save image. Please try again.',
        severity: 'error'
      });
    }
  };

  // Sync image with server
  const syncImageWithServer = async (photoId, imageData, aiImageData) => {
    if (!caseData || !photoId || !imageData || !aiImageData) {
      console.error('Missing data for syncing');
      return null;
    }

    try {
      setSyncingImage(true);

      console.log(`Syncing image for ${photoId} with server...`);

      // Prepare image data for API
      const imagePayload = {
        id: photoId,
        data: imageData,
        aiData: aiImageData,
        latitude: location.latitude,
        longitude: location.longitude,
        timestamp: new Date().toISOString()
      };

      // First, get the latest max version from the server
      let maxVersion;
      try {
        const maxVersionResponse = await caseService.getMaxVersion(caseData.web_reference_number);
        const serverMaxVersion = maxVersionResponse.maxVersion ? maxVersionResponse.maxVersion : '2';

        console.log(`Got max version from server: ${serverMaxVersion}`);

        // Reassignment already creates the next-version placeholder row.
        maxVersion = String(serverMaxVersion);
        console.log(`Using reassignment version: ${maxVersion}`);
      } catch (versionError) {
        console.error('Error getting max version from server:', versionError);
        // Fall back to default version if server request fails
        maxVersion = '2';
        console.log(`Falling back to default version ${maxVersion} for syncing reassigned image`);
      }

      // Add version information to the payload using the maxVersion state
      const enhancedPayload = {
        ...imagePayload,
        version: maxVersion,
        versionPath: `V${maxVersion}`,
        isReassigned: true,
        updateExisting: true
      };

      // Call API to sync image with version information
      // Add additional flags to ensure proper versioning
      enhancedPayload.isReassigned = true;
      enhancedPayload.updateExisting = true;

      // Create the expected URLs with version path using maxVersion state
      const versionPath = `V${maxVersion}`;
      const normalizedPhotoType = imagePayload.id.toLowerCase().replace(/ /g, '_');
      const azureUrl = buildAzureImageUrl(
        caseData.web_reference_number,
        versionPath,
        normalizedPhotoType
      );
      const fileUrl = `/uploads/${caseData.web_reference_number}/${versionPath}/${normalizedPhotoType}.jpg`;

      console.log(`Created URLs with version path ${versionPath}:`);
      console.log(`- Azure URL: ${azureUrl}`);
      console.log(`- File URL: ${fileUrl}`);

      // Add these to the payload
      enhancedPayload.azureUrl = azureUrl;
      enhancedPayload.fileUrl = fileUrl;
      enhancedPayload.expectedAzureUrl = azureUrl;
      enhancedPayload.expectedFileUrl = fileUrl;

      // Add explicit database update information
      enhancedPayload.dbUpdateInfo = {
        table: 'media',
        whereConditions: {
          webrefno: caseData.web_reference_number,
          version: maxVersion,
          phototype: normalizedPhotoType
        },
        updateValues: {
          azureurl: azureUrl,
          fileurl: fileUrl,
          latitude: imagePayload.latitude || null,
          longitude: imagePayload.longitude || null,
          capturedat: imagePayload.timestamp || new Date().toISOString(),
          capturedby: sessionStorage.getItem('username') || 'anonymous',
          updatedat: new Date().toISOString()
        }
      };

      // Store the URLs in state for reference
      console.log(`Using Azure URL: ${azureUrl} with version path: ${versionPath}`);

      console.log('Using existing syncImage function with enhanced payload for reassigned image');
      const response = await caseService.syncImage(caseData.web_reference_number, enhancedPayload);

      console.log('Image synced successfully');

      // Check if the response contains an Azure URL
      if (response?.data?.data?.azureUrl) {
        // Get the Azure URL - use the formatted URL if available, otherwise use the original
        const responseAzureUrl = response.data.data.formattedAzureUrl || response.data.data.azureUrl;
        const originalAzureUrl = response.data.data.azureUrl;

        // Get the version from the response or use default
        const versionFromResponse = response?.data?.data?.version;

        // Use response version or default
        let version;
        if (versionFromResponse) {
          // Use version from response
          version = versionFromResponse.startsWith('V') ? versionFromResponse : `V${versionFromResponse}`;
          console.log(`Using version from response: ${version} for ${photoId}`);
        } else {
          // Default to V2 as fallback
          version = 'V2';
          console.log(`Using default version ${version} for ${photoId}`);
        }

        console.log(`Received Azure URL from server: ${originalAzureUrl}`);
        console.log(`Using formatted URL with version ${version}: ${responseAzureUrl}`);

        // Update the capturedImages state with the formatted URL
        setCapturedImages(prev => ({
          ...prev,
          [photoId]: responseAzureUrl // Use the already formatted URL
        }));

        // Also update the reassignedImages array to include the Azure URL
        setReassignedImages(prevImages => {
          const updatedImages = prevImages.map(img => {
            if (img.originalPhotoType === photoId) {
              return {
                ...img,
                azureUrl: responseAzureUrl,
                version: parseInt(versionFromResponse || '2')
              };
            }
            return img;
          });

          // Force a re-render to update UI
          setTimeout(() => setReassignedImages([...updatedImages]), 100);

          return updatedImages;
        });

        console.log(`State updated for ${photoId} with Azure URL`);
      }

      setSyncingImage(false);

      // Check if all required images are captured
      checkAllImagesCaptured();

      // Return the response for further processing
      return response;
    } catch (err) {
      console.error('Error syncing image with server:', err);
      setSyncingImage(false);

      // Show error message
      setSnackbar({
        open: true,
        message: 'Failed to sync image with server. It will be retried automatically.',
        severity: 'warning'
      });

      throw err;
    }
  };

  // No longer saving images to localStorage
  // Images are handled directly through the API
  const saveImageToState = (photoId, imageData) => {
    try {
      // Update the captured images state
      setCapturedImages(prev => ({
        ...prev,
        [photoId]: imageData
      }));

      console.log(`Image for ${photoId} saved to state`);
    } catch (err) {
      console.error('Error saving image to state:', err);
    }
  };
  // State for delete confirmation dialog
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState({
    open: false,
    photoType: null
  });

  // State for version history dialog
  const [versionHistoryDialog, setVersionHistoryDialog] = useState({
    open: false,
    photoType: null,
    currentVersion: null,
    versions: []
  });

  // Handle opening delete confirmation dialog
  const handleOpenDeleteDialog = (photoType) => {
    setDeleteConfirmDialog({
      open: true,
      photoType
    });
  };

  // Handle closing delete confirmation dialog
  const handleCloseDeleteDialog = () => {
    setDeleteConfirmDialog({
      open: false,
      photoType: null
    });
  };

  // Handle delete image button click
  const handleDeleteImage = async (photoType) => {
    try {
      setSnackbar({
        open: true,
        message: 'Deleting image...',
        severity: 'info'
      });

      const webRefNo = caseData.web_reference_number;
      console.log(`Deleting image for ${photoType}`);

      // Use the version from the image object if available
      const imageToDelete = reassignedImages.find(img => img.originalPhotoType === photoType);
      const version = imageToDelete?.version || 1;
      console.log(`Using version ${version} for deletion`);

      // Delete from backend
      await caseService.deleteImage(webRefNo, photoType, version);

      // Update UI state by modifying the reassignedImages array
      const updatedReassignedImages = reassignedImages.map(img => {
        if (img.originalPhotoType === photoType) {
          // Clear the Azure URL to reflect the database update
          return {
            ...img,
            azureUrl: ''
          };
        }
        return img;
      });

      // Update the reassignedImages state
      setReassignedImages(updatedReassignedImages);

      // Force a re-render to update the UI
      setTimeout(() => setReassignedImages([...updatedReassignedImages]), 100);

      // Also update capturedImages state
      const updatedCapturedImages = { ...capturedImages };
      delete updatedCapturedImages[photoType];
      setCapturedImages(updatedCapturedImages);

      // Close the confirmation dialog
      handleCloseDeleteDialog();

      setSnackbar({
        open: true,
        message: 'Image deleted successfully',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error deleting image:', error);

      // Close the confirmation dialog
      handleCloseDeleteDialog();

      setSnackbar({
        open: true,
        message: 'Failed to delete image: ' + (error.message || 'Unknown error'),
        severity: 'error'
      });
    }
  };



  // Check if all required images are captured
  const checkAllImagesCaptured = () => {
    if (!reassignedImages || reassignedImages.length === 0) return false;

    // Check if all reassigned images have been captured or already have valid Azure URLs
    return reassignedImages.every(image =>
      capturedImages[image.originalPhotoType] ||
      (image.azureUrl && image.azureUrl.includes(`/V${image.version}/`))
    );
  };

  // Get progress percentage safely
  const getProgressPercentage = () => {
    if (reassignedImages.length === 0) return 0;

    // Count images that are either in capturedImages state or have valid Azure URLs
    const capturedCount = reassignedImages.reduce((count, image) => {
      // Count if it's in capturedImages OR if it has a valid azureUrl with version
      if (capturedImages[image.originalPhotoType] ||
        (image.azureUrl && image.azureUrl.includes(`/V${image.version}/`))) {
        return count + 1;
      }
      return count;
    }, 0);

    console.log(`Progress calculation: ${capturedCount} of ${reassignedImages.length} images available`);
    return (capturedCount / reassignedImages.length) * 100;
  };

  // Handle submitting the inspection
  const handleSubmitInspection = async () => {
    if (!checkAllImagesCaptured()) {
      setSnackbar({
        open: true,
        message: 'Please capture all required images before submitting',
        severity: 'warning'
      });
      return;
    }

    try {
      setSubmitting(true);

      // Call API to submit reassigned images
      const response = await caseService.submitReassignedImages(caseData.web_reference_number);

      console.log('Submission response:', response);

      if (response && response.status === 'success') {
        setShowSuccessDialog(true);
      } else {
        throw new Error('Submission failed: ' + (response?.message || 'Unknown error'));
      }

      setSubmitting(false);
    } catch (err) {
      console.error('Error submitting inspection:', err);
      setSnackbar({
        open: true,
        message: 'Failed to submit inspection. Please try again.',
        severity: 'error'
      });
      setSubmitting(false);
    }
  };

  // Handle closing the remarks dialog
  const handleCloseRemarksDialog = () => {
    setShowRemarksDialog(false);
  };

  // We're now handling redirection directly in the useEffect with window.location.href

  // Format Azure URL to handle versioned paths
  const formatAzureUrl = (url, photoType, version = '2') => {
    if (!url) return '';

    console.log('Formatting URL:', url, 'for photo type:', photoType, 'with version:', version);

    // If the URL already contains the version folder (V1, V2, etc.), return it as is
    if (url.includes('/V1/') || url.includes('/V2/') || url.includes('/V3/')) {
      console.log('URL already contains version folder, returning as is:', url);
      return url;
    }

    // Format version string
    const versionStr = `V${version}`;

    // Insert the version folder into the URL
    if (url.includes('/uploads/')) {
      // For local file URLs
      const parts = url.split('/');
      const webRefNo = parts[2]; // After /uploads/
      const filename = parts[parts.length - 1];

      const newUrl = `/uploads/${webRefNo}/${versionStr}/${filename}`;
      console.log('Formatted local URL:', newUrl);
      return newUrl;
    } else if (url.includes('blob.core.windows.net')) {
      // For Azure blob storage URLs
      const urlParts = url.split('/');
      const filename = urlParts[urlParts.length - 1];
      const containerPart = url.split('/evidensretailcases')[0];
      const webRefNo = urlParts[urlParts.length - 2]; // Assuming webRefNo is the second-to-last part

      const newUrl = `${containerPart}/evidensretailcases8080/${webRefNo}/${versionStr}/${filename}`;
      console.log('Formatted Azure URL:', newUrl);
      return newUrl;
    }

    // If we can't parse the URL, return it unchanged
    return url;
  };

  // Render loading state
  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>Loading case data...</Typography>
      </Box>
    );
  }

  // Render error state
  if (error) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', p: 3 }}>
        <Typography variant="h5" color="error" gutterBottom>Error</Typography>
        <Typography>{error}</Typography>
        <Button component={Link} to="/" variant="contained" sx={{ mt: 3 }}>
          Return to Dashboard
        </Button>
      </Box>
    );
  }

  if (redirectToProcessed || caseData?.status === 'Processed') {
    return (
      <Box sx={{ p: 2, minHeight: '100vh', display: 'grid', placeItems: 'center', bgcolor: 'background.default' }}>
        <Paper variant="outlined" sx={{ p: { xs: 3, sm: 4 }, maxWidth: 500, textAlign: 'center', borderRadius: 2 }}>
          <CheckCircle color="success" sx={{ fontSize: 56, mb: 1.5 }} />
          <Typography variant="h5" color="primary" gutterBottom>Inspection Under Review</Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            Thank you for submitting the reassigned photos. Our team is reviewing your case.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Reference Number: <strong>{caseData?.web_reference_number}</strong>
          </Typography>
        </Paper>
      </Box>
    );
  }

  const imageIsCaptured = image => Boolean(
    capturedImages[image.originalPhotoType]
    || (image.azureUrl && image.azureUrl.includes(`/V${image.version}/`))
  );
  const completedPhotos = reassignedImages.filter(imageIsCaptured).length;
  const currentPhotoDetails = reassignedImages.find(image => image.originalPhotoType === currentPhoto);
  const currentPhotoNumber = reassignedImages.findIndex(image => image.originalPhotoType === currentPhoto) + 1;
  const currentPhotoLabel = currentPhotoDetails
    ? t('inspection.photoLabels.' + currentPhotoDetails.originalPhotoType, { defaultValue: currentPhotoDetails.photoType.replace(/_/g, ' ') })
    : '';
  const detailSections = [
    {
      title: t('caseDetails.policyInfo'),
      icon: <Description fontSize="small" />,
      items: [
        { label: t('caseDetails.referenceNumber'), value: caseData?.web_reference_number },
        { label: 'Quote Number', value: caseData?.quote_number },
        { label: t('caseDetails.startDate'), value: formatDate(caseData?.policy_start_date) },
        { label: t('caseDetails.endDate'), value: formatDate(caseData?.policy_expiry_date) },
        { label: t('caseDetails.policyCoverType'), value: caseData?.policy_cover_type },
        { label: 'Cover Details', value: caseData?.cover_details },
      ],
    },
    {
      title: t('caseDetails.vehicleInfo'),
      icon: <DirectionsCar fontSize="small" />,
      items: [
        { label: 'Plate Character', value: caseData?.vehicle_plate_character },
        { label: t('caseDetails.plateNumber'), value: caseData?.vehicle_plate },
        { label: t('caseDetails.engine'), value: caseData?.engine_number },
        { label: t('caseDetails.chassisNumber'), value: caseData?.chassis_number },
        { label: t('caseDetails.make'), value: caseData?.make },
        { label: t('caseDetails.model'), value: caseData?.model },
        { label: 'Body Type', value: caseData?.body_type },
        { label: 'Manufacturing Year', value: caseData?.manufacturing_year },
      ],
    },
  ];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', maxWidth: 1120, mx: 'auto', p: { xs: 1, sm: 1.5, md: 2 }, overflowX: 'hidden' }}>
      {/* Company header */}
      <Paper sx={{
        px: { xs: 1.25, sm: 1.75 },
        py: 1,
        mb: 1.5,
        borderRadius: 1,
        border: 1,
        borderColor: 'divider',
        bgcolor: 'background.paper'
      }}>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          <img
            src={logo}
            alt="Company Logo"
            style={{
              height: '26px',
              width: 'auto'
            }}
          />
          <Box sx={{ minWidth: 0 }}>
            <Typography
              component="h1"
              variant="h6"
              sx={{
                display: { xs: 'none', sm: 'block' },
                fontSize: { xs: '0.95rem', sm: '1.05rem' },
                whiteSpace: 'nowrap'
              }}
            >
              {t('app.title')}
            </Typography>
          </Box>
          <Box sx={{ ml: 'auto', flexShrink: 0 }}>
            <LanguageSwitcher compact />
          </Box>
        </Box>
      </Paper>

      {/* Main content */}
      <Box>
        {/* Inspection Completion Status */}
        <Paper
          elevation={0}
          sx={{
            p: 1.5,
            mb: 1.5,
            borderRadius: 1,
            border: 1,
            borderColor: 'divider',
            bgcolor: 'background.paper',
            color: 'text.primary'
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              {checkAllImagesCaptured()
                ? t('inspection.reassignment.complete')
                : t('inspection.reassignment.progress')}
            </Typography>
            <Typography variant="h6">
              {Math.round(getProgressPercentage())}%
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={getProgressPercentage()}
            sx={{ my: 1, height: 6, borderRadius: 3 }}
            color={checkAllImagesCaptured() ? "success" : "primary"}
          />
          <Typography variant="body2">
            {checkAllImagesCaptured()
              ? t('inspection.reassignment.allCaptured')
              : (() => {
                // Count images that are either in capturedImages state or have valid URLs
                const capturedCount = reassignedImages.reduce((count, image) => {
                  if (capturedImages[image.originalPhotoType] ||
                    (image.imageUrl && image.imageUrl.includes(`/V${image.version}/`))) {
                    return count + 1;
                  }
                  return count;
                }, 0);
                return t('inspection.reassignment.photosRemaining', { captured: capturedCount, total: reassignedImages.length });
              })()
            }
          </Typography>
        </Paper>

        {/* Location Bar */}
        <Paper sx={{
          px: 1.25,
          py: 0.75,
          mb: 1.5,
          borderRadius: 1,
          border: 1,
          borderColor: 'divider',
          bgcolor: 'info.light',
          color: 'text.primary'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <LocationOn color="primary" fontSize="small" sx={{ mr: 1 }} />
            <Typography variant="caption" fontWeight={600} noWrap>
              {location.locationName}
            </Typography>
          </Box>
        </Paper>

        <Box sx={{ mb: 1.5 }}>
          {detailSections.map(section => (
            <Accordion key={section.title} disableGutters elevation={0} sx={{ mb: 1, border: 1, borderColor: 'divider', borderRadius: '8px !important', bgcolor: 'background.paper', '&:before': { display: 'none' }, overflow: 'hidden' }}>
              <AccordionSummary expandIcon={<ExpandMore />} sx={{ minHeight: 48, px: 1.5, '& .MuiAccordionSummary-content': { alignItems: 'center', gap: 1 } }}>
                <Box sx={{ width: 28, height: 28, display: 'grid', placeItems: 'center', borderRadius: 1, bgcolor: 'info.light', color: 'primary.main' }}>
                  {section.icon}
                </Box>
                <Typography variant="h6">{section.title}</Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0, borderTop: 1, borderColor: 'divider' }}>
                <KeyValueGrid items={section.items} />
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>

        <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: 1.5, borderRadius: 1, border: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, mb: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 32, height: 32, display: 'grid', placeItems: 'center', borderRadius: 1, bgcolor: 'info.light', color: 'primary.main' }}>
                <CameraAlt fontSize="small" />
              </Box>
              <Typography variant="h5">Photos to retake</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" fontWeight={600}>{completedPhotos} / {reassignedImages.length}</Typography>
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }, gap: 1 }}>
            {reassignedImages.map(image => {
              const captured = imageIsCaptured(image);
              const capturedUrl = capturedImages[image.originalPhotoType] || (captured ? image.azureUrl : null);
              const label = t('inspection.photoLabels.' + image.originalPhotoType, { defaultValue: image.photoType.replace(/_/g, ' ') });
              return (
                <Paper key={image.photoType} variant="outlined" sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, minHeight: 82, borderColor: captured ? 'success.main' : 'divider' }}>
                  <Box component="img" src={capturedUrl || getCarIconPath(image.originalPhotoType)} alt="" sx={{ width: 60, height: 54, flexShrink: 0, objectFit: capturedUrl ? 'cover' : 'contain', borderRadius: 1, bgcolor: 'background.default', p: capturedUrl ? 0 : 0.5 }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body1" fontWeight={600}>{label}</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.35, color: captured ? 'success.main' : 'text.secondary' }}>
                      {captured ? <CheckCircle fontSize="small" /> : <AccessTime fontSize="small" />}
                      <Typography variant="caption" fontWeight={600}>{captured ? 'Captured' : t('inspection.status.pending')}</Typography>
                    </Box>
                    {remarks[image.id] && <Typography variant="caption" color="warning.dark" sx={{ display: 'block', mt: 0.25 }}>{remarks[image.id]}</Typography>}
                  </Box>
                  {captured ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <Tooltip title="Retake photo"><IconButton onClick={() => handleOpenCamera(image.originalPhotoType)} size="small" aria-label={`Retake ${label}`}><CameraAlt fontSize="small" /></IconButton></Tooltip>
                      <Tooltip title="Delete photo"><IconButton onClick={() => handleOpenDeleteDialog(image.originalPhotoType)} size="small" aria-label={`Delete ${label}`}><Delete fontSize="small" /></IconButton></Tooltip>
                    </Box>
                  ) : (
                    <Tooltip title={`Capture ${label}`}>
                      <IconButton onClick={() => handleOpenCamera(image.originalPhotoType)} aria-label={`Capture ${label}`} sx={{ flexShrink: 0, width: 40, height: 40, bgcolor: 'primary.main', color: 'primary.contrastText', '&:hover': { bgcolor: 'primary.dark' } }}>
                        <CameraAlt fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Paper>
              );
            })}
          </Box>
        </Paper>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
          <Button variant="contained" color="primary" size="large" disabled={!checkAllImagesCaptured() || submitting} onClick={handleSubmitInspection} sx={{ borderRadius: 0.5, px: { xs: 3, sm: 6 }, width: { xs: '100%', sm: 'auto' }, minWidth: { sm: 220 }, fontWeight: 600, textTransform: 'none' }} startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : (checkAllImagesCaptured() ? <CheckCircle /> : null)}>
            {submitting ? t('inspection.buttons.submitting') : t('inspection.reassignment.submitComplete')}
          </Button>
        </Box>

        {/* Policy Details - New section */}
        <Paper sx={{
          display: 'none',
          p: { xs: 2, sm: 3 },
          mb: 3,
          borderRadius: 2,
          boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
          border: `1px solid ${'rgba(255, 255, 255, 0.1)'}`,
          background: `linear-gradient(135deg, rgba(25, 118, 210, 0.02) 0%, rgba(25, 118, 210, 0.08) 100%)`
        }}>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            mb: 1.5,
            pb: 1.5,
            borderBottom: `1px solid ${'rgba(255, 255, 255, 0.1)'}`
          }}>
            <Avatar sx={{
              bgcolor: 'rgba(25, 118, 210, 0.1)',
              color: theme.palette.primary.main,
              mr: 1.5
            }}>
              <Description />
            </Avatar>
            <Typography variant="h6" fontWeight={600}>
              {t('caseDetails.policyInfo')}
            </Typography>
          </Box>
          <Grid container spacing={2.5}>
            <Grid item xs={6} sm={6} md={3}>
              <Paper sx={{
                p: 1.5,
                height: '100%',
                borderRadius: 2,
                border: `1px solid ${'rgba(255, 255, 255, 0.1)'}`,
                bgcolor: 'background.paper'
              }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {t('caseDetails.policyNumber')}
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {caseData?.policy_number || 'N/A'}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} sm={6} md={3}>
              <Paper sx={{
                p: 1.5,
                height: '100%',
                borderRadius: 2,
                border: `1px solid ${'rgba(255, 255, 255, 0.1)'}`,
                bgcolor: 'background.paper'
              }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {t('caseDetails.policyHolder')}
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {caseData?.policy_holder || 'N/A'}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} sm={6} md={3}>
              <Paper sx={{
                p: 1.5,
                height: '100%',
                borderRadius: 2,
                border: `1px solid ${'rgba(255, 255, 255, 0.1)'}`,
                bgcolor: 'background.paper'
              }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {t('caseDetails.startDate')}
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {caseData?.policy_start_date ? formatDate(caseData.policy_start_date) : 'N/A'}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} sm={6} md={3}>
              <Paper sx={{
                p: 1.5,
                height: '100%',
                borderRadius: 2,
                border: `1px solid ${'rgba(255, 255, 255, 0.1)'}`,
                bgcolor: 'background.paper'
              }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {t('caseDetails.endDate')}
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {caseData?.policy_end_date ? formatDate(caseData.policy_end_date) : 'N/A'}
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Paper>

        {/* Vehicle Details - Enhanced for mobile */}
        <Paper sx={{
          display: 'none',
          p: { xs: 2, sm: 3 },
          mb: 3,
          borderRadius: 2,
          boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
          border: `1px solid ${'rgba(255, 255, 255, 0.1)'}`,
          background: `linear-gradient(135deg, rgba(25, 118, 210, 0.02) 0%, rgba(25, 118, 210, 0.08) 100%)`
        }}>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            mb: 1.5,
            pb: 1.5,
            borderBottom: `1px solid ${'rgba(255, 255, 255, 0.1)'}`
          }}>
            <Avatar sx={{
              bgcolor: 'rgba(25, 118, 210, 0.1)',
              color: theme.palette.primary.main,
              mr: 1.5
            }}>
              <DirectionsCar />
            </Avatar>
            <Typography variant="h6" fontWeight={600}>
              {t('caseDetails.vehicleInfo')}
            </Typography>
          </Box>
          <Grid container spacing={2.5}>
            <Grid item xs={6} sm={6} md={3}>
              <Paper sx={{
                p: 1.5,
                height: '100%',
                borderRadius: 2,
                border: `1px solid ${'rgba(255, 255, 255, 0.1)'}`,
                bgcolor: 'background.paper'
              }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {t('caseDetails.make')}
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {caseData?.make || 'N/A'}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} sm={6} md={3}>
              <Paper sx={{
                p: 1.5,
                height: '100%',
                borderRadius: 2,
                border: `1px solid ${'rgba(255, 255, 255, 0.1)'}`,
                bgcolor: 'background.paper'
              }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {t('caseDetails.model')}
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {caseData?.model || 'N/A'}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} sm={6} md={3}>
              <Paper sx={{
                p: 1.5,
                height: '100%',
                borderRadius: 2,
                border: `1px solid ${'rgba(255, 255, 255, 0.1)'}`,
                bgcolor: 'background.paper'
              }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {t('caseDetails.plateNumber')}
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {caseData?.vehicle_plate || 'N/A'}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} sm={6} md={3}>
              <Paper sx={{
                p: 1.5,
                height: '100%',
                borderRadius: 2,
                border: `1px solid ${'rgba(255, 255, 255, 0.1)'}`,
                bgcolor: 'background.paper'
              }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {t('caseDetails.chassisNumber')}
                </Typography>
                <Typography variant="body1" fontWeight={600} sx={{
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  wordBreak: 'break-all'
                }}>
                  {caseData?.chassis_number || 'N/A'}
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Paper>

        {/* Reassigned images grid - Updated to match InspectionPage UI */}
        <Paper sx={{
          display: 'none',
          p: { xs: 2, sm: 3 },
          mb: 2,
          borderRadius: 2,
          boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
          border: `1px solid ${'rgba(255, 255, 255, 0.1)'}`,
          background: `linear-gradient(135deg, rgba(25, 118, 210, 0.02) 0%, rgba(25, 118, 210, 0.08) 100%)`
        }}>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            mb: 2,
            pb: 1.5,
            borderBottom: `1px solid ${'rgba(255, 255, 255, 0.1)'}`
          }}>
            <Avatar sx={{
              bgcolor: 'rgba(25, 118, 210, 0.1)',
              color: theme.palette.primary.main,
              mr: 1.5
            }}>
              <CameraAlt />
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight={600} sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                {t('inspection.reassignment.title')}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {(() => {
                  // Count images that are either in capturedImages state or have valid URLs
                  const capturedCount = reassignedImages.reduce((count, image) => {
                    if (capturedImages[image.originalPhotoType] ||
                      (image.imageUrl && image.imageUrl.includes(`/V${image.version}/`))) {
                      return count + 1;
                    }
                    return count;
                  }, 0);
                  return t('inspection.reassignment.photosRemaining', { captured: capturedCount, total: reassignedImages.length });
                })()}
              </Typography>
            </Box>
          </Box>

          <Grid container spacing={2.5}>
            {reassignedImages.map((image) => (
              <Grid item xs={12} sm={6} md={4} key={image.photoType}>
                <Card sx={{
                  borderRadius: 2,
                  boxShadow: (image.azureUrl && image.azureUrl.includes(`/V${image.version}/`)) ?
                    `0 4px 20px ${alpha(theme.palette.success.main, 0.15)}` :
                    '0 4px 20px rgba(0,0,0,0.05)',
                  border: (image.azureUrl && image.azureUrl.includes(`/V${image.version}/`)) ?
                    `1px solid rgba(76, 175, 80, 0.3)` :
                    `1px solid rgba(255, 255, 255, 0.1)`,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'all 0.3s ease-in-out',
                  overflow: 'hidden',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: (image.azureUrl && image.azureUrl.includes(`/V${image.version}/`)) ?
                      '0 8px 24px rgba(76, 175, 80, 0.2)' :
                      '0 8px 24px rgba(0,0,0,0.1)'
                  }
                }}>
                  {/* Render image if either Azure URL or image URL is available */}
                  {(image.azureUrl || image.imageUrl) ? (
                    <Box sx={{ position: 'relative' }}>
                      {image.azureUrl ? (
                        <img
                          src={`${image.azureUrl}${image.azureUrl.includes('?') ? '&' : '?'}cache=${new Date().getTime()}`}
                          alt={image.photoType}
                          style={{
                            width: '100%',
                            height: '180px',
                            objectFit: 'cover',
                            borderTopLeftRadius: '8px',
                            borderTopRightRadius: '8px'
                          }}
                          onLoad={() => console.log('Image loaded successfully:', image.photoType)}
                          onError={(e) => {
                            console.error('Azure image failed to load:', e.target.src);

                            // Use a fallback image if Azure URL fails
                            console.log('Using fallback image for', image.originalPhotoType);
                            e.target.src = getCarIconPath(image.originalPhotoType);
                          }}
                        />
                      ) : (
                        <img
                          src={`${image.imageUrl}${image.imageUrl.includes('?') ? '&' : '?'}cache=${new Date().getTime()}`}
                          alt={image.photoType}
                          style={{
                            width: '100%',
                            height: '180px',
                            objectFit: 'cover',
                            borderTopLeftRadius: '8px',
                            borderTopRightRadius: '8px'
                          }}
                          onError={(e) => {
                            console.error('Image failed to load:', e.target.src);

                            // Use a fallback image if URL fails
                            console.log('Using fallback image for', image.originalPhotoType);
                            e.target.src = getCarIconPath(image.originalPhotoType);
                          }}
                        />
                      )}
                      <Box sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        background: 'linear-gradient(to bottom, rgba(0,0,0,0) 70%, rgba(0,0,0,0.7) 100%)',
                        borderTopLeftRadius: '8px',
                        borderTopRightRadius: '8px',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'flex-end',
                        p: 1.5
                      }}>
                        <Typography variant="subtitle1" fontWeight={600} color="white" sx={{ textShadow: '0px 1px 2px rgba(0,0,0,0.5)' }}>
                          {t('inspection.photoLabels.' + image.originalPhotoType, { defaultValue: image.photoType.replace(/_/g, ' ') })}
                        </Typography>
                        {remarks[image.id] && (
                          <Typography variant="caption" color="white" sx={{
                            mt: 0.5,
                            display: 'flex',
                            alignItems: 'center',
                            textShadow: '0px 1px 2px rgba(0,0,0,0.5)'
                          }}>
                            <Warning fontSize="small" sx={{ mr: 0.5, color: theme.palette.warning.light }} />
                            {remarks[image.id]}
                          </Typography>
                        )}
                      </Box>
                      <Box sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        display: 'flex',
                        gap: 1
                      }}>
                        <Tooltip title="Retake Photo">
                          <IconButton
                            onClick={() => handleOpenCamera(image.originalPhotoType)}
                            sx={{
                              bgcolor: 'rgba(255, 255, 255, 0.8)',
                              '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.9)' },
                              boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
                            }}
                            size="small"
                          >
                            <CameraAlt fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {/* Show delete icon for both captured images and loaded images */}
                        {(capturedImages[image.originalPhotoType] ||
                          (image.azureUrl && image.azureUrl.includes(`/V${image.version}/`))) && (
                            <Tooltip title="Delete Image">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleOpenDeleteDialog(image.originalPhotoType)}
                                sx={{
                                  bgcolor: 'rgba(255, 255, 255, 0.8)',
                                  '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.9)' },
                                  boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
                                }}
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                      </Box>
                    </Box>
                  ) : (
                    <CardActionArea
                      onClick={() => handleOpenCamera(image.photoType)}
                      sx={{
                        height: '180px',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        background: `linear-gradient(135deg, rgba(25, 118, 210, 0.05) 0%, rgba(25, 118, 210, 0.15) 100%)`,
                        position: 'relative',
                        overflow: 'hidden'
                      }}
                    >
                      <Box
                        sx={{
                          position: 'absolute',
                          width: '120%',
                          height: '120%',
                          top: '-10%',
                          left: '-10%',
                          background: 'radial-gradient(circle, transparent 20%, rgba(25, 118, 210, 0.03) 20%, rgba(25, 118, 210, 0.03) 80%, transparent 80%, transparent)',
                          backgroundSize: '20px 20px',
                          opacity: 0.5,
                          animation: 'ripple 4s infinite linear',
                          '@keyframes ripple': {
                            '0%': {
                              transform: 'rotate(0deg)'
                            },
                            '100%': {
                              transform: 'rotate(360deg)'
                            }
                          }
                        }}
                      />
                      <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        zIndex: 1
                      }}>
                        <Box
                          component="img"
                          src={getCarIconPath(image.photoType)}
                          alt={image.photoType.replace(/_/g, ' ')}
                          sx={{
                            width: 160,
                            height: 'auto',
                            maxHeight: 120,
                            objectFit: 'contain',
                            bgcolor: 'rgba(255, 255, 255, 0.9)',
                            border: '2px solid rgba(25, 118, 210, 0.3)',
                            borderRadius: 2,
                            p: 2,
                            mb: 2,
                            boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                          }}
                        />
                        <Box sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: 'rgba(25, 118, 210, 0.9)',
                          color: 'white',
                          borderRadius: 5,
                          px: 2,
                          py: 0.75,
                          boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                        }}>
                          <CameraAlt fontSize="small" sx={{ mr: 0.5 }} />
                          <Typography variant="button" sx={{ fontWeight: 600 }}>
                            {t('inspection.buttons.capture')} {t('inspection.photoLabels.' + image.originalPhotoType, { defaultValue: image.photoType.replace(/_/g, ' ') })}
                          </Typography>
                        </Box>
                      </Box>
                    </CardActionArea>
                  )}
                  <CardContent sx={{
                    p: { xs: 2, sm: 2.5 },
                    flexGrow: 1,
                    bgcolor: capturedImages[image.photoType] ? 'rgba(76, 175, 80, 0.05)' : 'background.paper'
                  }}>
                    {remarks[image.id] ? (
                      <Box sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        mb: 1.5,
                        p: 1,
                        borderRadius: 1,
                        bgcolor: 'rgba(255, 193, 7, 0.1)',
                        border: '1px solid rgba(255, 193, 7, 0.3)'
                      }}>
                        <Description fontSize="small" sx={{ mr: 0.5, color: theme.palette.warning.main, mt: 0.25 }} />
                        <Typography variant="caption" color="text.secondary">
                          <strong>Remark:</strong> {remarks[image.id]}
                        </Typography>
                      </Box>
                    ) : (
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        {t('inspection.reassignment.pleaseCapture')}
                      </Typography>
                    )}
                    {image.remarkedBy && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {t('inspection.reassignment.remarkedBy', { name: image.remarkedBy })}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Paper>

        {/* Submit button - Enhanced UI */}
        <Box sx={{ display: 'none' }}>
          <Button
            variant="contained"
            color={checkAllImagesCaptured() ? "success" : "primary"}
            size="large"
            startIcon={checkAllImagesCaptured() ? <CheckCircle /> : <CloudUpload />}
            onClick={handleSubmitInspection}
            disabled={!checkAllImagesCaptured() || submitting}
            sx={{
              py: 1.5,
              px: 4,
              borderRadius: 2,
              fontSize: '1rem',
              fontWeight: 600,
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 6px 16px rgba(0,0,0,0.2)'
              },
              transition: 'all 0.3s ease'
            }}
          >
            {submitting ? (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                {t('inspection.buttons.submitting')}
              </Box>
            ) : (
              checkAllImagesCaptured() ? t('inspection.reassignment.submitComplete') : t('inspection.reassignment.submitStandard')
            )}
          </Button>
        </Box>

      </Box>

      {/* Camera Dialog - Modernized with full screen view optimized for mobile */}
      <Dialog
        open={false}
        fullWidth
        fullScreen={true}
        PaperProps={{
          sx: { borderRadius: 0, overflow: 'hidden' }
        }}
      >
        <DialogTitle sx={{
          bgcolor: theme.palette.primary.main,
          color: 'white',
          borderBottom: `1px solid ${'rgba(255, 255, 255, 0.1)'}`,
          pb: 2
        }}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center">
              <CameraAlt sx={{ mr: 1 }} />
              <Typography variant="h6" fontWeight={600}>
                {currentPhoto && t('inspection.photoLabels.' + currentPhoto, { defaultValue: currentPhoto.replace(/_/g, ' ') })}
              </Typography>
            </Box>
            <IconButton onClick={handleCloseCamera} size="small" sx={{ color: 'white' }}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0, position: 'relative', bgcolor: 'black' }}>
          {/* Location indicator */}
          <Box sx={{
            position: 'absolute',
            top: 16,
            left: 16,
            zIndex: 10,
            display: 'flex',
            alignItems: 'center',
            bgcolor: 'rgba(0, 0, 0, 0.6)',
            color: 'white',
            borderRadius: 2,
            px: 1.5,
            py: 0.5
          }}>
            <LocationOn fontSize="small" sx={{ mr: 0.5 }} />
            <Typography variant="body2" noWrap sx={{ maxWidth: '200px' }}>
              {location.locationName}
            </Typography>
          </Box>

          {/* Camera view - maximized and optimized for mobile */}
          <Box sx={{
            width: '100%',
            height: { xs: 'calc(100vh - 120px)', sm: 'calc(100vh - 120px)' },
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            bgcolor: 'black',
            position: 'relative'
          }}>
            {!capturedImageData ? (
              <video
                ref={videoRef}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  maxWidth: '100vw',
                  maxHeight: '100vh'
                }}
                autoPlay
                playsInline
                muted // Required for iOS Safari
              />
            ) : (
              <img
                src={capturedImageData}
                alt="Captured"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain'
                }}
              />
            )}

            {/* Car reference information */}
            <Box
              sx={{
                position: 'absolute',
                bottom: 16,
                left: 16,
                right: 16,
                zIndex: 10,
                bgcolor: 'rgba(0, 0, 0, 0.7)',
                color: 'white',
                p: 1.5,
                borderRadius: 2,
                display: 'flex',
                flexDirection: 'column',
                gap: 0.5
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <DirectionsCar fontSize="small" />
                <Typography variant="body2" fontWeight="bold">
                  {caseData?.make} {caseData?.model}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography variant="caption" color="grey.400">Plate:</Typography>
                  <Typography variant="caption" fontWeight="medium">{caseData?.vehicle_plate || 'N/A'}</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography variant="caption" color="grey.400">Chassis:</Typography>
                  <Typography variant="caption" fontWeight="medium">{caseData?.chassis_number || 'N/A'}</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography variant="caption" color="grey.400">Ref:</Typography>
                  <Typography variant="caption" fontWeight="medium">{caseData?.web_reference_number}</Typography>
                </Box>
              </Box>
            </Box>
          </Box>
          <canvas ref={canvasRef} style={{ display: 'none' }} />
        </DialogContent>
        <DialogActions sx={{ p: 2, justifyContent: 'center', bgcolor: 'black' }}>
          {!capturedImageData ? (
            <Button
              variant="contained"
              color="primary"
              onClick={handleCaptureImage}
              startIcon={<CameraAlt />}
              sx={{
                borderRadius: 30,
                px: 4,
                py: 1.5,
                fontSize: '1.1rem',
                boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                width: { xs: '80%', sm: 'auto' }
              }}
            >
              {t('inspection.buttons.capture')}
            </Button>
          ) : (
            <Box sx={{ display: 'flex', gap: 2, width: '100%', justifyContent: 'center' }}>
              <Button
                variant="contained"
                color="error"
                onClick={() => {
                  setCapturedImageData(null);
                  setCapturedAiImageData(null);
                }}
                disabled={syncingImage}
                sx={{
                  borderRadius: 30,
                  px: 3,
                  py: 1.5,
                  fontSize: '1rem',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                }}
              >
                {t('inspection.buttons.retake')}
              </Button>
              <Button
                variant="contained"
                color="success"
                onClick={handleConfirmImage}
                startIcon={<CheckCircle />}
                disabled={syncingImage}
                sx={{
                  borderRadius: 30,
                  px: 3,
                  py: 1.5,
                  fontSize: '1rem',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                }}
              >
                {t('common.confirm')}
              </Button>
            </Box>
          )}
        </DialogActions>
      </Dialog>

      {/* Full-screen camera matching the primary inspection flow. */}
      <Dialog
        open={cameraOpen}
        fullScreen
        PaperProps={{
          sx: {
            borderRadius: 0,
            overflow: 'hidden',
            bgcolor: 'black',
            height: '100vh',
            '@supports (height: 100dvh)': { height: '100dvh' }
          }
        }}
      >
        <DialogContent sx={{ p: '0 !important', position: 'relative', bgcolor: 'black', overflow: 'hidden' }}>
          <video
            ref={videoRef}
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'contain', background: '#000' }}
            autoPlay
            playsInline
            muted
          />

          {!cameraReady && (
            <Box sx={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', bgcolor: 'black' }}>
              <Box sx={{ textAlign: 'center', color: 'white' }}>
                <CircularProgress color="inherit" size={34} />
                <Typography variant="body2" sx={{ mt: 1.5 }}>Starting camera…</Typography>
              </Box>
            </Box>
          )}

          <Box sx={{ position: 'absolute', left: '8%', right: '8%', top: { xs: '25%', sm: '22%' }, bottom: { xs: '25%', sm: '23%' }, border: '2px solid rgba(255,255,255,0.9)', borderRadius: 2, boxShadow: '0 0 0 9999px rgba(0,0,0,0.08)', pointerEvents: 'none' }} />

          <Box sx={{ position: 'absolute', inset: '0 0 auto 0', zIndex: 2, color: 'white', px: 2, pt: 'max(14px, env(safe-area-inset-top))', pb: 5, background: 'linear-gradient(180deg, rgba(0,0,0,.88) 0%, rgba(0,0,0,.55) 65%, transparent 100%)' }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,.75)', fontWeight: 700 }}>
                  PHOTO {currentPhotoNumber || 1} OF {reassignedImages.length}
                </Typography>
                <Typography variant="h6" fontWeight={700} lineHeight={1.25}>{currentPhotoLabel}</Typography>
                <Typography variant="body2" sx={{ mt: 0.5, color: 'rgba(255,255,255,.88)', maxWidth: 520 }}>
                  {remarks[currentPhotoDetails?.id] || t('inspection.reassignment.pleaseCapture')}
                </Typography>
              </Box>
              <IconButton onClick={handleCloseCamera} aria-label="Close camera" sx={{ color: 'white', bgcolor: 'rgba(0,0,0,.45)', '&:hover': { bgcolor: 'rgba(0,0,0,.65)' } }}>
                <Close />
              </IconButton>
            </Box>
          </Box>

          <Box sx={{ position: 'absolute', top: { xs: 150, sm: 130 }, left: '50%', transform: 'translateX(-50%)', zIndex: 3, display: 'flex', alignItems: 'center', bgcolor: 'rgba(0,0,0,.62)', color: 'white', borderRadius: 10, px: 1.25, py: 0.6, maxWidth: 'calc(100% - 32px)' }}>
            <LocationOn fontSize="small" sx={{ mr: 0.5 }} />
            <Typography variant="caption" noWrap>{location.locationName}</Typography>
          </Box>

          <Box sx={{ position: 'absolute', inset: 'auto 0 0 0', zIndex: 3, display: 'grid', justifyItems: 'center', color: 'white', pt: 6, pb: 'max(18px, env(safe-area-inset-bottom))', background: 'linear-gradient(0deg, rgba(0,0,0,.9) 0%, rgba(0,0,0,.56) 62%, transparent 100%)' }}>
            <IconButton
              onClick={handleCaptureImage}
              disabled={!cameraReady}
              aria-label={`Capture ${currentPhotoLabel || 'photo'}`}
              sx={{ width: 76, height: 76, bgcolor: 'white', color: 'primary.main', border: '5px solid rgba(255,255,255,.38)', backgroundClip: 'padding-box', boxShadow: '0 4px 18px rgba(0,0,0,.35)', '&:hover': { bgcolor: 'grey.100' }, '&.Mui-disabled': { bgcolor: 'grey.500', color: 'grey.700' } }}
            >
              <CameraAlt sx={{ fontSize: 32 }} />
            </IconButton>
            <Typography variant="caption" fontWeight={700} sx={{ mt: 0.75 }}>Take photo</Typography>
          </Box>
          <canvas ref={canvasRef} style={{ display: 'none' }} />
        </DialogContent>
      </Dialog>

      {/* Full-screen photo review matching the primary inspection flow. */}
      <Dialog
        open={showConfirmDialog}
        onClose={syncingImage ? undefined : handleRetakeImage}
        fullScreen
        PaperProps={{ sx: { bgcolor: 'black', borderRadius: 0, height: '100dvh', display: 'flex', flexDirection: 'column' } }}
      >
        <DialogTitle sx={{ px: 2, py: 1.25, bgcolor: 'black', color: 'white', borderBottom: '1px solid rgba(255,255,255,.14)' }}>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,.7)' }}>Review photo</Typography>
          <Typography variant="h6" fontWeight={700}>{currentPhotoLabel}</Typography>
        </DialogTitle>
        <DialogContent sx={{ p: '0 !important', bgcolor: 'black', position: 'relative', flex: 1, minHeight: 0, overflow: 'hidden' }}>
          {capturedImageData && <img src={capturedImageData} alt={`Captured ${currentPhotoLabel || 'inspection photo'}`} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'contain', display: 'block' }} />}
          {syncingImage && (
            <Box sx={{ position: 'absolute', inset: 0, zIndex: 2, display: 'grid', placeItems: 'center', bgcolor: 'rgba(0,0,0,.7)', color: 'white' }}>
              <Box sx={{ textAlign: 'center' }}>
                <CircularProgress color="inherit" />
                <Typography fontWeight={700} sx={{ mt: 1.5 }}>Saving photo…</Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, pb: 'max(16px, env(safe-area-inset-bottom))', gap: 1, justifyContent: 'center', bgcolor: 'black', borderTop: '1px solid rgba(255,255,255,.14)' }}>
          <Button variant="outlined" onClick={handleRetakeImage} startIcon={<CameraAlt />} disabled={syncingImage} sx={{ borderRadius: 1.5, color: 'white', borderColor: 'rgba(255,255,255,.6)', minHeight: 48 }}>
            {t('inspection.buttons.retake')}
          </Button>
          <Button variant="contained" color="primary" onClick={handleConfirmImage} startIcon={syncingImage ? <CircularProgress size={18} color="inherit" /> : <Save />} disabled={syncingImage} sx={{ borderRadius: 1.5, minHeight: 48, minWidth: 150 }}>
            {syncingImage ? 'Saving...' : 'Use photo'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Remarks dialog */}
      <Dialog
        open={showRemarksDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 1.5, margin: { xs: 1, sm: 2 }, width: { xs: 'calc(100% - 16px)', sm: 480 } }
        }}
      >
        <DialogTitle sx={{ borderBottom: 1, borderColor: 'divider', p: 2.5 }}>
          <Box display="flex" alignItems="center">
            <Warning color="warning" fontSize="small" sx={{ mr: 1 }} />
            <Typography variant="h4">Photos need to be retaken</Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 2.5 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Recapture the requested photos using the inspector's remarks below.
          </Typography>
          <Box sx={{ display: 'grid', gap: 1 }}>
            {reassignedImages.map(image => (
              <Paper key={image.photoType} variant="outlined" sx={{ p: 1.5, bgcolor: 'background.default' }}>
                <Typography variant="body2" fontWeight={700}>
                  {t('inspection.photoLabels.' + image.originalPhotoType, { defaultValue: image.photoType.replace(/_/g, ' ') })}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {remarks[image.id] || t('inspection.reassignment.pleaseCapture')}
                </Typography>
              </Paper>
            ))}
          </Box>
          <Paper variant="outlined" sx={{ mt: 2.5, p: 1.75, bgcolor: 'background.default' }}>
            <Typography variant="caption" color="text.secondary">Need help? Customer support</Typography>
            <Typography component="a" href="tel:08046512500" variant="body1" fontWeight={700} color="primary.main" sx={{ display: 'block', mt: 0.25, textDecoration: 'none' }}>
              080 4651 2500
            </Typography>
          </Paper>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 2, borderTop: 1, borderColor: 'divider' }}>
          <Button onClick={handleCloseRemarksDialog} variant="contained" fullWidth>
            I understand — retake photos
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation dialog */}
      <Dialog
        open={deleteConfirmDialog.open}
        onClose={handleCloseDeleteDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          Confirm Delete
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Are you sure you want to delete this image? This will reset the image and you'll need to capture it again.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} color="primary">
            Cancel
          </Button>
          <Button
            onClick={() => handleDeleteImage(deleteConfirmDialog.photoType)}
            color="error"
            variant="contained"
            startIcon={<Delete />}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success dialog */}
      <Dialog
        open={showSuccessDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2, margin: { xs: 1, sm: 2 }, width: { xs: 'calc(100% - 16px)', sm: 'auto' } } }}
      >
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 3, textAlign: 'center' }}>
            <CheckCircle color="success" sx={{ fontSize: 72, mb: 2 }} />
            <Typography variant="h5" color="success.main" gutterBottom>
              Inspection Submitted Successfully!
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Thank you for submitting the reassigned photos. Our team will review them shortly.
            </Typography>
            <Typography variant="body2" color="primary" fontWeight={700} sx={{ mt: 2 }}>
              {caseData?.web_reference_number}
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ReassignmentPage;
